import { NavLink } from "react-router-dom";
import * as Icons from "lucide-react";
import { NAV } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { VoraLogo } from "@/components/brand/VoraLogo";
import { DEMO_ACTIVO } from "@/demo/modo";

// MODO DEMO · Alcance 1 (sep-2026): LISTA BLANCA de lo que se ofrece en el menú de la
// demostración — exactamente los módulos que tienen datos ficticios y se ven "en
// operación". Ninguna ruta queda bloqueada: las demás pantallas abren igual si se teclea
// la URL (los permisos del demo están completos), simplemente no se anuncian hasta el
// Alcance 2.
const VISIBLES_EN_DEMO = new Set([
  "/dashboard", "/canal-denuncia",
  "/r/prospectos", "/r/productos_credito",
  "/sofom/cartera", "/lineas", "/r/pagos", "/r/garantias",
  "/sofom/cobranza",
  "/r/clientes", "/clientes-360", "/r/expedientes", "/r/beneficiarios", "/r/operaciones",
  "/pld", "/capacitacion", "/otras-obligaciones", "/r/casos",
  "/r/reportes", "/calendario", "/documentos-legales",
  "/configuracion-entidad", "/usuarios", "/auditoria",
]);

// feb-2026 · iter 07-25 · Filtro POR ITEM por giro. Complementa `hasArea` (server-side)
// con granularidad item-level para casos donde un área "compartida" arrastra items que
// no aplican a todos los giros (ej. CONDUSEF vive en área "cumplimiento" que es común,
// pero solo aplica a SOFOM). El campo `giros` en nav.js es opcional: sin él, aplica a todos.
function itemAplicaAlGiro(item, giro) {
  if (!item.giros) return true;              // default = aplica a todos los giros
  return item.giros.includes(giro);
}

export function SidebarContent({ onNavigate }) {
  const { user, giro, hasArea, moduloBloqueado } = useAuth();
  const role = user?.role;

  return (
    <div className="flex h-full flex-col bg-primary text-slate-100">
      <div className="flex items-center border-b border-white/10 px-5 py-4">
        <VoraLogo variant="compact" tone="light" />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {NAV.filter((g) =>
          (!g.roles || g.roles.includes(role)) &&
          !(g.rolesNot || []).includes(role) &&
          hasArea(g.area) &&
          !moduloBloqueado(g.planFeature) &&
          itemAplicaAlGiro(g, giro)         // filtro giro a nivel de GRUPO
        ).map((g) => {
          const items = g.items.filter((i) =>
            !(role === "super_admin" && (i.pii || i.saasHidden)) &&
            !moduloBloqueado(i.planFeature) &&
            (!i.roles || i.roles.includes(role)) &&
            !(i.rolesNot || []).includes(role) &&
            !(DEMO_ACTIVO && !VISIBLES_EN_DEMO.has(i.to)) &&
            itemAplicaAlGiro(i, giro)       // filtro giro a nivel de ITEM
          );
          if (items.length === 0) return null;
          return (
            <div key={g.group || g.items[0]?.to} className="mb-4">
              {g.group && (
                <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-sage/60">
                  {g.group}
                </p>
              )}
              {items.map((item) => {
                const Icon = Icons[item.icon] || Icons.Circle;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/dashboard"}
                    onClick={onNavigate}
                    data-testid={`nav-${item.to.replace(/^\//, "").replace(/\//g, "-") || "home"}`}
                    className={({ isActive }) =>
                      `group mb-0.5 flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-accent text-white shadow-sm"
                          : "text-slate-300 hover:bg-white/10 hover:text-white"
                      }`
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                    <span className="truncate">{(role === "super_admin" && item.saasLabel) || item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-white/10 px-5 py-3 text-[11px] text-slate-400">
        Herramienta de gestión regulatoria. Sujeta a parametrización conforme a políticas internas.
      </div>
    </div>
  );
}
